import React, { useState, useEffect } from 'react';
import { useAnalytics } from '../hooks/useAnalytics';
import './Theme.css';

/**
 * Theme Settings Component
 * Allows users to select and customize the application theme
 */
const Theme = () => {
  const { trackAction, trackFeature } = useAnalytics();
  const [selectedTheme, setSelectedTheme] = useState('dark');
  const [accentColor, setAccentColor] = useState('#3B82F6');
  const [fontSize, setFontSize] = useState('medium');
  const [themeMode, setThemeMode] = useState('dark'); // 'light' or 'dark'
  const [useSystemSettings, setUseSystemSettings] = useState(false);

  // Helper function to adjust color brightness
  const adjustColorBrightness = (hex, percent) => {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Convert to RGB
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);
    
    // Adjust brightness
    r = Math.max(0, Math.min(255, r + (r * percent / 100)));
    g = Math.max(0, Math.min(255, g + (g * percent / 100)));
    b = Math.max(0, Math.min(255, b + (b * percent / 100)));
    
    // Convert back to hex
    const toHex = (n) => {
      const hex = Math.round(n).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  // Load saved theme preferences on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'dark';
    const savedAccentType = localStorage.getItem('accentColorType');
    let savedAccent = localStorage.getItem('accentColor') || '#3B82F6';
    const savedFontSize = localStorage.getItem('fontSize') || 'medium';
    const savedThemeMode = localStorage.getItem('themeMode') || 'dark';
    const savedUseSystemSettings = localStorage.getItem('useSystemSettings') === 'true';
    
    // Handle adaptive accent color
    if (savedAccentType === 'adaptive') {
      const isDark = savedTheme !== 'light' && savedTheme !== 'monokai' && savedTheme !== 'catppuccin-latte' && savedTheme !== 'light-high-contrast';
      savedAccent = isDark ? '#FFFFFF' : '#000000';
    }
    
    setSelectedTheme(savedTheme);
    setAccentColor(savedAccent);
    setFontSize(savedFontSize);
    setThemeMode(savedThemeMode);
    setUseSystemSettings(savedUseSystemSettings);
    
    // Apply system theme if enabled
    if (savedUseSystemSettings) {
      applySystemTheme();
    } else {
      // Apply theme
      applyTheme(savedTheme, savedAccent, savedFontSize);
    }
    
    // Ensure CSS variables are set immediately
    document.documentElement.style.setProperty('--accent-color', savedAccent);
    document.documentElement.style.setProperty('--accent-primary', savedAccent);
    document.documentElement.style.setProperty('--palette-action-primary-default', savedAccent);
    document.documentElement.style.setProperty('--palette-action-link-default', savedAccent);
    document.documentElement.style.setProperty('--mia-purple-primary', savedAccent);
  }, []);

  // Listen for system theme changes
  useEffect(() => {
    if (!useSystemSettings) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      const systemMode = e.matches ? 'dark' : 'light';
      setThemeMode(systemMode);
      applyThemeModeChange(systemMode);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [useSystemSettings]);

  // Apply system theme
  const applySystemTheme = () => {
    const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const systemMode = isDark ? 'dark' : 'light';
    setThemeMode(systemMode);
    applyThemeModeChange(systemMode);
  };

  // Apply theme mode change
  const applyThemeModeChange = (mode) => {
    const defaultTheme = mode === 'dark' ? 'dark' : 'light';
    setSelectedTheme(defaultTheme);
    localStorage.setItem('theme', defaultTheme);
    localStorage.setItem('themeMode', mode);
    applyTheme(defaultTheme, accentColor, fontSize);
  };

  // Handle theme mode toggle
  const handleThemeModeChange = (mode) => {
    setThemeMode(mode);
    setUseSystemSettings(false);
    localStorage.setItem('useSystemSettings', 'false');
    applyThemeModeChange(mode);
    trackAction('settings', 'theme_mode_changed', { mode });
  };

  // Handle use system settings toggle
  const handleUseSystemSettingsChange = (checked) => {
    setUseSystemSettings(checked);
    localStorage.setItem('useSystemSettings', checked.toString());
    
    if (checked) {
      applySystemTheme();
      trackAction('settings', 'system_theme_enabled');
    } else {
      trackAction('settings', 'system_theme_disabled');
    }
  };

  // Apply theme to document
  const applyTheme = (theme, accent, size) => {
    // Remove all theme classes first
    document.documentElement.classList.remove('dark-mode', 'theme-noctis', 'theme-github', 'theme-synthwave', 'theme-hackerx-green', 'theme-hackerx-ocean', 'theme-monokai', 'theme-catppuccin-latte', 'theme-catppuccin-frappe', 'theme-catppuccin-macchiato', 'theme-catppuccin-mocha', 'theme-high-contrast', 'theme-light-high-contrast');
    document.body.classList.remove('dark-mode', 'theme-noctis', 'theme-github', 'theme-synthwave', 'theme-hackerx-green', 'theme-hackerx-ocean', 'theme-monokai', 'theme-catppuccin-latte', 'theme-catppuccin-frappe', 'theme-catppuccin-macchiato', 'theme-catppuccin-mocha', 'theme-high-contrast', 'theme-light-high-contrast');
    
    // Apply the selected theme
    if (theme === 'light' || theme === 'monokai' || theme === 'catppuccin-latte' || theme === 'light-high-contrast') {
      // Light themes
      if (theme === 'light-high-contrast') {
        document.documentElement.classList.add('theme-light-high-contrast');
        document.body.classList.add('theme-light-high-contrast');
      } else if (theme !== 'light') {
        document.documentElement.classList.add(`theme-${theme}`);
        document.body.classList.add(`theme-${theme}`);
      }
    } else {
      // Dark themes
      if (theme === 'dark' || theme === 'midnight' || theme === 'aurora' || theme === 'high-contrast') {
        // Basic dark themes without specific theme class
        document.documentElement.classList.add('dark-mode');
        document.body.classList.add('dark-mode');
        if (theme === 'high-contrast') {
          document.documentElement.classList.add('theme-high-contrast');
          document.body.classList.add('theme-high-contrast');
        }
      } else {
        // Custom themes with specific classes
        document.documentElement.classList.add('dark-mode', `theme-${theme}`);
        document.body.classList.add('dark-mode', `theme-${theme}`);
      }
    }
    
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.style.setProperty('--accent-color', accent);
    document.documentElement.style.setProperty('--accent-primary', accent);
    document.documentElement.style.setProperty('--palette-action-primary-default', accent);
    document.documentElement.style.setProperty('--palette-action-link-default', accent);
    document.documentElement.style.setProperty('--mia-purple-primary', accent);
  };

  // Handle theme change
  const handleThemeChange = (theme) => {
    setSelectedTheme(theme);
    localStorage.setItem('theme', theme);
    applyTheme(theme, accentColor, fontSize);
    
    // Re-apply accent color if it's adaptive
    const savedAccentType = localStorage.getItem('accentColorType');
    if (savedAccentType === 'adaptive') {
      const isDark = theme !== 'light' && theme !== 'monokai' && theme !== 'catppuccin-latte' && theme !== 'light-high-contrast';
      const adaptiveColor = isDark ? '#FFFFFF' : '#000000';
      setAccentColor(adaptiveColor);
      document.documentElement.style.setProperty('--accent-color', adaptiveColor);
      document.documentElement.style.setProperty('--accent-primary', adaptiveColor);
      document.documentElement.style.setProperty('--palette-action-primary-default', adaptiveColor);
      document.documentElement.style.setProperty('--palette-action-link-default', adaptiveColor);
      document.documentElement.style.setProperty('--mia-purple-primary', adaptiveColor);
    }
    
    trackAction('settings', 'theme_changed', { theme });
    trackFeature('theme_customization', { theme });
  };

  // Handle accent color change
  const handleAccentColorChange = (colorObj) => {
    let color = colorObj.value;
    
    // Handle adaptive color based on theme
    if (color === 'adaptive') {
      const isDark = selectedTheme !== 'light' && selectedTheme !== 'monokai' && selectedTheme !== 'catppuccin-latte' && selectedTheme !== 'light-high-contrast';
      color = isDark ? colorObj.darkValue : colorObj.lightValue;
    }
    
    setAccentColor(color);
    localStorage.setItem('accentColor', color);
    localStorage.setItem('accentColorType', colorObj.value); // Store the type (adaptive or actual color)
    
    // Update all accent-related CSS variables
    document.documentElement.style.setProperty('--accent-color', color);
    document.documentElement.style.setProperty('--accent-primary', color);
    document.documentElement.style.setProperty('--palette-action-primary-default', color);
    document.documentElement.style.setProperty('--palette-action-link-default', color);
    document.documentElement.style.setProperty('--mia-purple-primary', color);
    
    // Calculate hover color (slightly darker)
    const hoverColor = adjustColorBrightness(color, -10);
    document.documentElement.style.setProperty('--accent-hover', hoverColor);
    document.documentElement.style.setProperty('--palette-action-primary-hover', hoverColor);
    document.documentElement.style.setProperty('--palette-action-link-hover', hoverColor);
    
    trackAction('settings', 'accent_color_changed', { color });
  };

  // Handle font size change
  const handleFontSizeChange = (size) => {
    setFontSize(size);
    localStorage.setItem('fontSize', size);
    const fontSizeMap = {
      'small': '14px',
      'medium': '16px',
      'large': '18px'
    };
    const fontSize = fontSizeMap[size];
    document.documentElement.style.setProperty('--base-font-size', fontSize);
    document.documentElement.style.setProperty('--main-size', fontSize);
    trackAction('settings', 'font_size_changed', { size });
  };

  const darkThemes = [
    {
      id: 'dark',
      name: 'Dark',
      description: 'Default dark theme with deep blacks',
      bgColor: '#0A0A0A',
      textColor: '#FFFFFF',
      preview: {
        background: 'linear-gradient(135deg, #0A0A0A 0%, #1C1C1C 100%)',
        textColor: '#FFFFFF',
        secondaryColor: '#888888',
        accentColor: '#3B82F6'
      }
    },
    {
      id: 'midnight',
      name: 'Midnight',
      description: 'Deep blue-black theme',
      bgColor: '#0F172A',
      textColor: '#F1F5F9',
      preview: {
        background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
        textColor: '#F1F5F9',
        secondaryColor: '#94A3B8',
        accentColor: '#60A5FA'
      }
    },
    {
      id: 'aurora',
      name: 'Aurora',
      description: 'Vibrant gradient theme',
      bgColor: '#667eea',
      textColor: '#FFFFFF',
      preview: {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
        textColor: '#FFFFFF',
        secondaryColor: '#F0E7FF',
        accentColor: '#FDE68A'
      }
    },
    {
      id: 'noctis',
      name: 'Noctis',
      description: 'Night sky inspired theme',
      bgColor: '#0d1117',
      textColor: '#E6EDF3',
      preview: {
        background: 'linear-gradient(135deg, #0d1117 0%, #1f2937 100%)',
        textColor: '#E6EDF3',
        secondaryColor: '#7D8590',
        accentColor: '#58A6FF'
      }
    },
    {
      id: 'github',
      name: 'GitHub Dark',
      description: 'GitHub dark mode theme',
      bgColor: '#0d1117',
      textColor: '#C9D1D9',
      preview: {
        background: 'linear-gradient(135deg, #0d1117 0%, #161b22 100%)',
        textColor: '#C9D1D9',
        secondaryColor: '#8B949E',
        accentColor: '#58A6FF'
      }
    },
    {
      id: 'synthwave',
      name: 'Synthwave',
      description: 'Retro 80s neon theme',
      bgColor: '#241b2f',
      textColor: '#FFFFFF',
      preview: {
        background: 'linear-gradient(135deg, #241b2f 0%, #34294f 100%)',
        textColor: '#FFFFFF',
        secondaryColor: '#FF6AD5',
        accentColor: '#72F1B8'
      }
    },
    {
      id: 'hackerx-green',
      name: 'Hacker Green',
      description: 'Matrix-style green terminal',
      bgColor: '#0d1b0d',
      textColor: '#00FF41',
      preview: {
        background: 'linear-gradient(135deg, #0d1b0d 0%, #1a2e1a 100%)',
        textColor: '#00FF41',
        secondaryColor: '#008F11',
        accentColor: '#00FF41'
      }
    },
    {
      id: 'hackerx-ocean',
      name: 'Hacker Ocean',
      description: 'Deep ocean blue theme',
      bgColor: '#001c2e',
      textColor: '#00D9FF',
      preview: {
        background: 'linear-gradient(135deg, #001c2e 0%, #003545 100%)',
        textColor: '#00D9FF',
        secondaryColor: '#0088AA',
        accentColor: '#00D9FF'
      }
    },
    {
      id: 'catppuccin-frappe',
      name: 'Catppuccin Frappé',
      description: 'Cool pastel dark theme',
      bgColor: '#303446',
      textColor: '#C6D0F5',
      preview: {
        background: 'linear-gradient(135deg, #303446 0%, #414559 100%)',
        textColor: '#C6D0F5',
        secondaryColor: '#A5ADCE',
        accentColor: '#8CAAEE'
      }
    },
    {
      id: 'catppuccin-macchiato',
      name: 'Catppuccin Macchiato',
      description: 'Smooth dark pastel theme',
      bgColor: '#24273a',
      textColor: '#CAD3F5',
      preview: {
        background: 'linear-gradient(135deg, #24273a 0%, #363a4f 100%)',
        textColor: '#CAD3F5',
        secondaryColor: '#A5ADCB',
        accentColor: '#8AADF4'
      }
    },
    {
      id: 'catppuccin-mocha',
      name: 'Catppuccin Mocha',
      description: 'Rich dark pastel theme',
      bgColor: '#1e1e2e',
      textColor: '#CDD6F4',
      preview: {
        background: 'linear-gradient(135deg, #1e1e2e 0%, #313244 100%)',
        textColor: '#CDD6F4',
        secondaryColor: '#A6ADC8',
        accentColor: '#89B4FA'
      }
    },
    {
      id: 'high-contrast',
      name: 'High Contrast',
      description: 'Maximum contrast for accessibility',
      bgColor: '#000000',
      textColor: '#FFFFFF',
      preview: {
        background: 'linear-gradient(135deg, #000000 0%, #1A1A1A 100%)',
        textColor: '#FFFFFF',
        secondaryColor: '#CCCCCC',
        accentColor: '#00FF00'
      }
    }
  ];

  const lightThemes = [
    {
      id: 'light',
      name: 'Light',
      description: 'Clean light theme for bright environments',
      bgColor: '#FFFFFF',
      textColor: '#0F172A',
      preview: {
        background: 'linear-gradient(135deg, #FFFFFF 0%, #F3F4F6 100%)',
        textColor: '#0F172A',
        secondaryColor: '#64748B',
        accentColor: '#2563EB'
      }
    },
    {
      id: 'monokai',
      name: 'Monokai',
      description: 'Classic Monokai light theme',
      bgColor: '#faf8f5',
      textColor: '#272822',
      preview: {
        background: 'linear-gradient(135deg, #faf8f5 0%, #f5f2ed 100%)',
        textColor: '#272822',
        secondaryColor: '#75715e',
        accentColor: '#66D9EF'
      }
    },
    {
      id: 'catppuccin-latte',
      name: 'Catppuccin Latte',
      description: 'Warm light pastel theme',
      bgColor: '#eff1f5',
      textColor: '#4C4F69',
      preview: {
        background: 'linear-gradient(135deg, #eff1f5 0%, #dce0e8 100%)',
        textColor: '#4C4F69',
        secondaryColor: '#6C6F85',
        accentColor: '#1e66f5'
      }
    },
    {
      id: 'light-high-contrast',
      name: 'Light High Contrast',
      description: 'Maximum contrast light theme for accessibility',
      bgColor: '#FFFFFF',
      textColor: '#000000',
      preview: {
        background: 'linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 100%)',
        textColor: '#000000',
        secondaryColor: '#333333',
        accentColor: '#0000FF'
      }
    }
  ];

  const accentColors = [
    { name: 'Mono', value: 'adaptive', darkValue: '#FFFFFF', lightValue: '#000000' },
    { name: 'Blue', value: '#1D4ED8' },
    { name: 'Purple', value: '#7C3AED' },
    { name: 'Green', value: '#047857' },
    { name: 'Orange', value: '#C2410C' },
    { name: 'Pink', value: '#BE185D' },
    { name: 'Teal', value: '#0F766E' }
  ];

  const fontSizes = [
    { id: 'small', name: 'Small', size: '14px' },
    { id: 'medium', name: 'Medium', size: '16px' },
    { id: 'large', name: 'Large', size: '18px' }
  ];

  const renderThemeCard = (theme) => (
    <button
      key={theme.id}
      className={`theme-card ${selectedTheme === theme.id ? 'active' : ''}`}
      onClick={() => handleThemeChange(theme.id)}
      type="button"
    >
      <div 
        className="theme-preview" 
        style={{ background: theme.preview.background }}
      >
        <div className="theme-preview-text">
          <span 
            className="preview-heading"
            style={{ color: theme.preview.textColor }}
          >
            Heading
          </span>
          <span 
            className="preview-line"
            style={{ color: theme.preview.secondaryColor }}
          >
            Sample text line
          </span>
          <span 
            className="preview-code-inline"
            style={{ 
              color: theme.preview.textColor,
              background: `${theme.preview.accentColor}33`
            }}
          >
            code()
          </span>
        </div>
      </div>
      <div className="theme-info">
        <h4>{theme.name}</h4>
        <p>{theme.description}</p>
      </div>
      {selectedTheme === theme.id && (
        <div className="theme-check">✓</div>
      )}
    </button>
  );

  return (
    <div className="theme-settings">
      <div className="theme-section">
        <h2 className="theme-section-title">Appearance</h2>
        <p className="theme-section-description">
          Customize the look and feel of your workspace
        </p>

        {/* Theme Mode Switch */}
        <div className="theme-mode-controls">
          <div className="theme-mode-switch">
            <button
              className={`theme-mode-button ${themeMode === 'light' ? 'active' : ''}`}
              onClick={() => handleThemeModeChange('light')}
              disabled={useSystemSettings}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="8" cy="8" r="3" fill="currentColor"/>
                <path d="M8 1V2M8 14V15M15 8H14M2 8H1M12.95 3.05L12.24 3.76M3.76 12.24L3.05 12.95M12.95 12.95L12.24 12.24M3.76 3.76L3.05 3.05" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              Light
            </button>
            <button
              className={`theme-mode-button ${themeMode === 'dark' ? 'active' : ''}`}
              onClick={() => handleThemeModeChange('dark')}
              disabled={useSystemSettings}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 1C4.134 1 1 4.134 1 8s3.134 7 7 7c.34 0 .673-.024 1-.07A6 6 0 019 3c0-.7.12-1.373.342-2A7.035 7.035 0 008 1z" fill="currentColor"/>
              </svg>
              Dark
            </button>
          </div>

          <label className="system-settings-checkbox">
            <input
              type="checkbox"
              checked={useSystemSettings}
              onChange={(e) => handleUseSystemSettingsChange(e.target.checked)}
            />
            <span>Use system settings</span>
          </label>
        </div>
      </div>

      {/* Dark Themes */}
      <div className="theme-section">
        <h3 className="theme-subsection-title">Dark Themes</h3>
        <div className="theme-grid">
          {darkThemes.map(renderThemeCard)}
        </div>
      </div>

      {/* Light Themes */}
      <div className="theme-section">
        <h3 className="theme-subsection-title">Light Themes</h3>
        <div className="theme-grid">
          {lightThemes.map(renderThemeCard)}
        </div>
      </div>

      {/* Accent Color */}
      <div className="theme-section">
        <h3 className="theme-subsection-title">Accent Color</h3>
        <div className="accent-colors">
          {accentColors.map((colorObj) => {
            const displayColor = colorObj.value === 'adaptive' 
              ? (selectedTheme !== 'light' && selectedTheme !== 'monokai' && selectedTheme !== 'catppuccin-latte' && selectedTheme !== 'light-high-contrast' ? colorObj.darkValue : colorObj.lightValue)
              : colorObj.value;
            const isActive = colorObj.value === 'adaptive' 
              ? localStorage.getItem('accentColorType') === 'adaptive'
              : accentColor === colorObj.value;
            
            return (
              <button
                key={colorObj.value}
                className={`accent-color-btn ${isActive ? 'active' : ''}`}
                onClick={() => handleAccentColorChange(colorObj)}
                style={{ backgroundColor: displayColor }}
                title={colorObj.name}
                type="button"
              >
                {isActive && (
                  <span className="accent-check">✓</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Font Size */}
      <div className="theme-section">
        <h3 className="theme-subsection-title">Font Size</h3>
        <div className="font-size-options">
          {fontSizes.map((size) => (
            <button
              key={size.id}
              className={`font-size-btn ${fontSize === size.id ? 'active' : ''}`}
              onClick={() => handleFontSizeChange(size.id)}
              type="button"
            >
              <span style={{ fontSize: size.size }}>{size.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Preview Section */}
      <div className="theme-section">
        <h3 className="theme-subsection-title">Preview</h3>
        <div className="theme-preview-box">
          <div className="preview-header">
            <h4>Sample Content</h4>
            <button 
              className="preview-button"
              style={{ backgroundColor: accentColor }}
              type="button"
            >
              Button
            </button>
          </div>
          <p className="preview-text">
            This is how your text will look with the selected theme and font size.
          </p>
          <div className="preview-code">
            <code>{`const theme = "${selectedTheme}";`}</code>
          </div>
        </div>
      </div>

      {/* Reset to Defaults */}
      <div className="theme-section">
        <button
          className="theme-reset-btn"
          onClick={() => {
            setSelectedTheme('dark');
            setAccentColor('#3B82F6');
            setFontSize('medium');
            localStorage.setItem('theme', 'dark');
            localStorage.setItem('accentColor', '#3B82F6');
            localStorage.setItem('fontSize', 'medium');
            applyTheme('dark', '#3B82F6', 'medium');
          }}
          type="button"
        >
          Reset to Defaults
        </button>
      </div>
    </div>
  );
};

export default Theme;
